import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import {
  ListToListMatchType,
  StringToListMatchType,
} from "@redotech/redo-flows-core-common/condition";
import { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum WorkOrderTriggerKind {
  ORDER = "order",
  REPLENISHMENT = "replenishment",
  MANUAL = "manual",
}

export enum WorkOrderTriggerSource {
  PRODUCT = "product",
  BOM = "bom",
  BOM_TAG = "bom_tag",
}

export enum WorkOrderFlowStepType {
  WORK_STEP = "work_order_work_step",
  CONDITION = "work_order_condition",
}

// Mirrors OmsConditionStep fields — used for trigger evaluation and in-flow branching
const WorkOrderCondition_ = z.object({
  conditionType: createEnum(OmsConditionType),
  matchType: z
    .union([createEnum(StringToListMatchType), createEnum(ListToListMatchType)])
    .optional(),
  matchValues: z.array(z.string()).optional(),
  matchValue: z.unknown().optional(),
  matchDate: z.date().optional(),
  matchKey: z.string().optional(),
  where: z.array(z.unknown()).optional(),
});
export const WorkOrderCondition: z.ZodType<WorkOrderCondition> =
  WorkOrderCondition_;
export interface WorkOrderCondition extends z.infer<
  typeof WorkOrderCondition_
> {}

// A leaf node: a single product condition
const WorkOrderTriggerLeafNode_ = WorkOrderCondition_.extend({
  nodeType: z.literal("leaf"),
});

// A group node: AND/OR combination of child nodes (leaf or nested group)
// Uses z.lazy() for the recursive children reference
const WorkOrderTriggerGroupNode_: z.ZodType<WorkOrderTriggerGroupNode> = z.lazy(
  () =>
    z.object({
      nodeType: z.literal("group"),
      conjunctionMode: createEnum(ConjunctionMode),
      children: z.array(WorkOrderTriggerNode_),
    }),
);

const WorkOrderTriggerNode_: z.ZodType<WorkOrderTriggerNode> = z.lazy(() =>
  z.union([WorkOrderTriggerLeafNode_, WorkOrderTriggerGroupNode_]),
);

export const WorkOrderTriggerNode: z.ZodType<WorkOrderTriggerNode> =
  WorkOrderTriggerNode_;
export type WorkOrderTriggerNode =
  | ({ nodeType: "leaf" } & WorkOrderCondition)
  | WorkOrderTriggerGroupNode;

export const WorkOrderTriggerGroupNode: z.ZodType<WorkOrderTriggerGroupNode> =
  WorkOrderTriggerGroupNode_;
export interface WorkOrderTriggerGroupNode {
  nodeType: "group";
  conjunctionMode: ConjunctionMode;
  children: WorkOrderTriggerNode[];
}

// Top-level trigger conditions: a root group evaluated against each line item
const WorkOrderFlowTriggerConditions_ = z.object({
  conjunctionMode: createEnum(ConjunctionMode),
  children: z.array(WorkOrderTriggerNode_),
});
export const WorkOrderFlowTriggerConditions: z.ZodType<WorkOrderFlowTriggerConditions> =
  WorkOrderFlowTriggerConditions_;
export interface WorkOrderFlowTriggerConditions {
  conjunctionMode: ConjunctionMode;
  children: WorkOrderTriggerNode[];
}

// A product or BOM that is consumed (input) or produced (output) during a step
const WorkOrderFlowStepProduct_ = z.object({
  kind: z.union([z.literal("product"), z.literal("bom")]),
  id: z.string(),
  quantity: z.number().int().positive(),
});
export const WorkOrderFlowStepProduct: z.ZodType<WorkOrderFlowStepProduct> =
  WorkOrderFlowStepProduct_;
export interface WorkOrderFlowStepProduct extends z.infer<
  typeof WorkOrderFlowStepProduct_
> {}

// A line item custom attribute surfaced to the worker during a step
const WorkOrderDisplayAttribute_ = z.object({
  label: z.string(),
  attributeKey: z.string(),
});
export const WorkOrderDisplayAttribute: z.ZodType<WorkOrderDisplayAttribute> =
  WorkOrderDisplayAttribute_;
export interface WorkOrderDisplayAttribute extends z.infer<
  typeof WorkOrderDisplayAttribute_
> {}

// The primary step type: a human task with inputs, outputs, and display info
const WorkOrderWorkStep_ = z.object({
  id: z.string(),
  type: z.literal(WorkOrderFlowStepType.WORK_STEP),
  title: z.string(),
  nextId: z.string().nullable(),
  displayAttributes: z.array(WorkOrderDisplayAttribute),
  inputs: z.array(WorkOrderFlowStepProduct),
  outputs: z.array(WorkOrderFlowStepProduct),
  stationId: z.string().nullable().optional(),
  instructions: z.string().optional(),
  printBarcodeOnComplete: z.boolean().optional(),
  showSiblingBatches: z.boolean().optional(),
});
export const WorkOrderWorkStep: z.ZodType<WorkOrderWorkStep> =
  WorkOrderWorkStep_;
export interface WorkOrderWorkStep extends z.infer<typeof WorkOrderWorkStep_> {}

// A binary branch step — chain multiple to create N-way branches
const WorkOrderConditionStep_ = z.object({
  id: z.string(),
  type: z.literal(WorkOrderFlowStepType.CONDITION),
  conditionType: createEnum(OmsConditionType),
  matchType: z
    .union([createEnum(StringToListMatchType), createEnum(ListToListMatchType)])
    .optional(),
  matchValues: z.array(z.string()).optional(),
  matchValue: z.unknown().optional(),
  matchDate: z.date().optional(),
  matchKey: z.string().optional(),
  where: z.array(z.unknown()).optional(),
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});
export const WorkOrderConditionStep: z.ZodType<WorkOrderConditionStep> =
  WorkOrderConditionStep_;
export interface WorkOrderConditionStep extends z.infer<
  typeof WorkOrderConditionStep_
> {}

const WorkOrderFlowStep_ = z.discriminatedUnion("type", [
  WorkOrderWorkStep_,
  WorkOrderConditionStep_,
]);
export const WorkOrderFlowStep: z.ZodType<WorkOrderFlowStep> =
  WorkOrderFlowStep_;
export type WorkOrderFlowStep = {} & z.infer<typeof WorkOrderFlowStep_>;

const WorkOrderFlow_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  description: z.string().optional(),
  enabled: z.boolean(),
  index: z.number().int(),
  triggerKind: createEnum(WorkOrderTriggerKind),
  triggerSource: createEnum(WorkOrderTriggerSource).optional(), // defaults to "product" for backwards compat
  triggerBomIds: z.array(z.string()).optional(), // BOM IDs when triggerSource is "bom"
  triggerBomTags: z.array(z.string()).optional(), // BOM tags when triggerSource is "bom_tag"
  triggerConditions: WorkOrderFlowTriggerConditions,
  maxWorkOrderQuantity: z.number().int().positive().optional(), // max quantity per work order before creating a new one
  batchMinSize: z.number().int().positive().optional(), // min batch size for replenishment trigger
  batchMaxSize: z.number().int().positive().optional(), // max batch size for replenishment trigger
  steps: z.array(WorkOrderFlowStep),
  skipPlacing: z.boolean().optional(), // When true, work order completes after last step without placing output
  autoPlaceStorageLocationId: z.string().optional(), // When set, auto-places output to this storage location on completion
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});
export const WorkOrderFlow: z.ZodType<WorkOrderFlow> = WorkOrderFlow_;
export interface WorkOrderFlow extends z.infer<typeof WorkOrderFlow_> {}
